import { PaymentTypeOptions } from "Types/state";
import { Store } from "src/store";
import signIn from "src/sdk/signIn";
import { OnlineShopper, StartOptions } from "src/types/window";
import { logError } from "src/utils/logger";
import { addGarageReporter } from "src/store/dynamicGarageReporters";
import initialize from "src/sdk/initialize";
import toggleOS from "src/sdk/toggleOS";
import { ApiVehicle } from "Types/api/Vehicle";

let store: Store;
let bootstrapped: boolean = false;

const queue: Array<() => void> = [];

const SDK: OnlineShopper = {
    APP_VERSION: process.env.APP_VERSION!,
    BUILD_NUMBER: process.env.BUILD_NUMBER!,

    // Initialize the app. Apart of the public interface due to how our test page works, but should not be used by 3rd parties
    async bootstrap(accountSlug: string, env: string, srpLink: string) {
        bootstrapped = false; // Reset to false so that our test page can call bootstrap more than once (to change accounts)

        store = await initialize(accountSlug, env, srpLink);

        bootstrapped = true;

        // Handle any start() or toggleOS() calls that were queued while we were bootstrapping
        while (queue.length > 0) {
            const command = queue.shift();
            command && command();
        }
    },

    // Previously relied on loader script to call this to start the app
    // Now only used to handle third party callbacks
    start(
        ...args:
            | [any, any]
            | [(vehicles: ApiVehicle[], os: any) => void, () => void]
            | [any, any, any, any]
            | [string, StartOptions, (vehicles: ApiVehicle[], os: any) => void, any]
    ) {
        const onGarageUpdate = args.length === 4 ? args[2] : args[0];
        const startedCallback = args.length === 4 ? args[3] : args[1];

        if (bootstrapped) {
            if (onGarageUpdate && typeof onGarageUpdate === "function") {
                addGarageReporter(onGarageUpdate);
            }

            if (startedCallback && typeof startedCallback === "function") {
                startedCallback();
            }
        } else {
            queue.unshift(() => window.OnlineShopper.start(onGarageUpdate, startedCallback));
        }
    },

    toggleOS(vin?: string, paymentType?: PaymentTypeOptions) {
        if (bootstrapped) {
            toggleOS(store, vin, paymentType).catch((e) => logError(e));
        } else {
            queue.push(async () => window.OnlineShopper.toggleOS(vin, paymentType));
        }
    },

    signIn() {
        if (bootstrapped) {
            signIn(store);
        } else {
            queue.push(async () => window.OnlineShopper.signIn());
        }
    },

    // Hard deprecation, no usages found
    toggleGarageFlyout() {
        logError("removed window.OnlineShopper method used: toggleGarageFlyout used.");
    },

    // Hard deprecation, no usages found
    updateGarageFlyout(_payload: any) {
        // Flyout should be configured in account settings
        logError("removed window.OnlineShopper method used: updateGarageFlyout used.");
    },

    // Hard deprecation, no usages found
    async updateLanguage(_language: string) {
        // We detect language based on parent page html lang attribute
        logError("removed window.OnlineShopper method used: updateLanguage used.");
    },
};

export default SDK;
